import { displayHandle } from '@/platform/users/displayHandle';

/** Relative timestamp for feed cards. Pass `nowMs` in tests; omit in UI (uses Date.now()). */
export function formatFeedTime(iso?: string, nowMs: number = Date.now()): string {
  if (!iso) return '';
  const date = new Date(iso);
  const diffMs = nowMs - date.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  // Fixed locale + UTC — default toLocaleDateString() differs Node vs browser and breaks hydration.
  return date.toLocaleDateString('en-US', { timeZone: 'UTC' });
}

export function authorHandle(userId: string): string {
  return `@${displayHandle(undefined, userId)}`;
}

export function authorInitial(userId: string): string {
  return (userId[0] ?? '?').toUpperCase();
}

export type FeedAuthorProfile = {
  displayName?: string;
  handle?: string;
  avatarUrl?: string;
};

/** Resolve feed author label, avatar initial, and photo from public profile data. */
export function resolveFeedAuthorDisplay(
  userId: string,
  profile?: FeedAuthorProfile | null,
  authPhotoUrl?: string | null,
) {
  const displayName = profile?.displayName?.trim();
  const handle = displayHandle(profile?.handle, userId);
  const avatarUrl = profile?.avatarUrl?.trim() || authPhotoUrl?.trim() || undefined;
  const name = displayName || `@${handle}`;
  const initial = (displayName?.[0] ?? handle[0] ?? userId[0] ?? '?').toUpperCase();
  return { name, handle, displayName: displayName || undefined, initial, avatarUrl };
}

export function formatFeedAuthorMeta(
  handle: string,
  displayName: string | undefined,
  time: string,
  meta?: string,
): string {
  const parts: string[] = [];
  if (displayName) parts.push(`@${handle}`);
  if (time) parts.push(time);
  if (meta) parts.push(meta);
  return parts.join(' · ');
}

export function excerpt(text: string, max = 220): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max).trimEnd()}…`;
}
